import { Link } from 'react-router-dom';
import BenefitIcon from '../components/BenefitIcon';
import { useEffect, useRef, useState } from 'react';
import { setPageMeta } from '../utils/setPageMeta';

const publicUrl = process.env.PUBLIC_URL || '';

// Custom hook for scroll animations
function useScrollAnimation(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return { ref, isVisible };
}

// 3D Tilt Card Component
function TiltCard({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = (y - centerY) / 10;
      const rotateY = (centerX - x) / 10;

      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
    };

    const handleMouseLeave = () => {
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div ref={cardRef} className={className} style={{ ...style, transition: 'transform 0.1s ease-out' }}>
      {children}
    </div>
  );
}

// Custom Cursor Component
function CustomCursor() {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
      
      const target = e.target as HTMLElement;
      const isInteractive = target.closest('a, button, .btn');
      setIsHovering(!!isInteractive);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      <div 
        className={`custom-cursor ${isHovering ? 'hovering' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      />
      <div 
        className={`custom-cursor-dot ${isHovering ? 'hovering' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      />
    </>
  );
}

export default function About() {
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const whoSection = useScrollAnimation(0.2);
  const teamSection = useScrollAnimation(0.2);
  const joinSection = useScrollAnimation(0.2);

  useEffect(() => {
    setPageMeta({
      title: 'About LeadingNG | Web3, Fintech & AI Software Company',
      description:
        'LeadingNG is a software company with 18+ years experience, focused on Web3, fintech and AI. We work with clients worldwide from Auckland, New Zealand.',
      canonicalPath: '/about-us',
      keywords:
        'About LeadingNG, software company Auckland, Web3 consultancy, fintech developers, AI integration, blockchain team, New Zealand software',
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = (window.scrollY / documentHeight) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <CustomCursor />
      <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} aria-hidden="true" />

      <section className="page-hero">
        <div className="hero-bg" style={{ transform: `translateY(${scrollY * 0.5}px)` }} />
        <div className="container text-center">
          <span className="page-hero-label animate-fade-in">About us</span>
          <h1 className="page-title animate-slide-up" style={{ animationDelay: '0.1s' }}>LeadingNG</h1>
          <p className="page-intro animate-fade-in" style={{ animationDelay: '0.3s' }}>
            A software company with over 18 years of experience—focused on <strong>Web3</strong>, <strong>Fintech</strong> and <strong>AI</strong>. We work with clients worldwide.
          </p>
        </div>
      </section>

      <section ref={whoSection.ref} className={`section who-section ${whoSection.isVisible ? 'visible' : ''}`}>
        <div className="container text-center">
          <span className="section-label scroll-reveal">Who we are</span>
          <h2 className="title scroll-reveal gradient-text" style={{ transitionDelay: '0.1s' }}>Expertise that delivers</h2>
          <p className="intro scroll-reveal" style={{ transitionDelay: '0.2s' }}>
            We bring proven expertise in software development, platform engineering and technical leadership. We introduce key technology solutions to meet the changing needs of dynamic businesses and are always ready to offer individual solutions—turning difficulties into challenges.
          </p>
          <div className="benefits-grid">
            <TiltCard className="benefit-item scroll-reveal" style={{ transitionDelay: '0.3s' }}>
              <div className="benefit-icon"><BenefitIcon name="team" /></div>
              <h3 className="benefit-title">Skilled team</h3>
              <div className="benefit-glow" />
            </TiltCard>
            <TiltCard className="benefit-item scroll-reveal" style={{ transitionDelay: '0.4s' }}>
              <div className="benefit-icon"><BenefitIcon name="agile" /></div>
              <h3 className="benefit-title">Agile approach</h3>
              <div className="benefit-glow" />
            </TiltCard>
            <TiltCard className="benefit-item scroll-reveal" style={{ transitionDelay: '0.5s' }}>
              <div className="benefit-icon"><BenefitIcon name="quality" /></div>
              <h3 className="benefit-title">High quality code</h3>
              <div className="benefit-glow" />
            </TiltCard>
            <TiltCard className="benefit-item scroll-reveal" style={{ transitionDelay: '0.6s' }}>
              <div className="benefit-icon"><BenefitIcon name="overheads" /></div>
              <h3 className="benefit-title">No overheads</h3>
              <div className="benefit-glow" />
            </TiltCard>
          </div>
        </div>
      </section>

      <section ref={teamSection.ref} className={`section team-section ${teamSection.isVisible ? 'visible' : ''}`}>
        <div className="container">
          <span className="section-label text-center scroll-reveal" style={{ display: 'block' }}>Meet the team</span>
          <h2 className="title text-center scroll-reveal gradient-text" style={{ transitionDelay: '0.1s' }}>Gus Chiechelski</h2>
          <div className="team-card scroll-reveal" style={{ transitionDelay: '0.2s' }}>
            <div className="team-photos" title="Hover to see another photo">
              <img src={`${publicUrl}/assets/images/team/gu-side.jpg`} alt="Gus Chiechelski" className="team-img team-img-default" loading="lazy" />
              <img src={`${publicUrl}/assets/images/team/gu-front.jpg`} alt="Gus Chiechelski" className="team-img team-img-hover" loading="lazy" />
            </div>
            <div className="team-info">
              <h3 className="team-role">CEO & Founder</h3>
              <p>
                Gus has over 18 years of experience in software development across different industries and company sizes, with an emphasis on introducing key technology solutions for dynamic business environments.
              </p>
              <p>
                He holds a B.Sc. in Computer Science from The Federal University of Rio Grande do Sul{' '}
                <a href="http://www.ufrgs.br/english/the-university/institutes-faculties-and-schools/institute-of-informatics" target="_blank" rel="noopener noreferrer">(UFRGS)</a>
                {' '}and an MBA from Auckland University of Technology{' '}
                <a href="http://www.aut.ac.nz/" target="_blank" rel="noopener noreferrer">(AUT)</a>.
                Entrepreneurial, self-taught and proactive, he is motivated by challenges and brings deep knowledge in <strong>TypeScript</strong>, <strong>Rust</strong>, Node.js, React, React Native, AWS, Terraform, serverless, PHP/Laravel and AI/RAG—with a focus on <strong>Web3</strong>, <strong>Fintech</strong> and <strong>AI</strong>.
              </p>
              <div className="team-social">
                <a href="https://nz.linkedin.com/in/gochiechelski" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
                </a>
                <a href="https://github.com/chiechelski" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section ref={joinSection.ref} className={`section join-section ${joinSection.isVisible ? 'visible' : ''}`}>
        <div className="container text-center">
          <span className="section-label scroll-reveal">Get in touch</span>
          <h2 className="title scroll-reveal gradient-text" style={{ transitionDelay: '0.1s' }}>Want to partner with us?</h2>
          <p className="intro scroll-reveal" style={{ transitionDelay: '0.2s' }}>Drop us a line or give us a ring. We'd love to hear from you.</p>
          <Link to="/contact-us" className="btn btn-cta btn-cta-primary magnetic-btn scroll-reveal" style={{ transitionDelay: '0.3s' }}>
            <span className="btn-text">Contact us today!</span>
            <span className="btn-bg" />
          </Link>
        </div>
      </section>

      <style>{`
        /* Custom Cursor */
        .custom-cursor,
        .custom-cursor-dot {
          position: fixed;
          pointer-events: none;
          z-index: 10000;
          transform: translate(-50%, -50%);
        }
        .custom-cursor {
          width: 40px;
          height: 40px;
          border: 2px solid var(--color-primary);
          border-radius: 50%;
          transition: width 0.3s ease, height 0.3s ease, border-color 0.3s ease;
          opacity: 0.5;
        }
        .custom-cursor.hovering {
          width: 60px;
          height: 60px;
          border-color: var(--color-secondary);
          opacity: 0.8;
        }
        .custom-cursor-dot {
          width: 6px;
          height: 6px;
          background: var(--color-primary);
          border-radius: 50%;
          transition: transform 0.15s ease, background 0.3s ease;
        }
        .custom-cursor-dot.hovering {
          transform: translate(-50%, -50%) scale(1.5);
          background: var(--color-secondary);
        }

        /* Scroll Progress Bar */
        .scroll-progress-bar {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          z-index: 9999;
          transition: width 0.1s ease-out;
          box-shadow: 0 0 10px var(--color-primary);
        }

        /* Hero animations */
        .animate-fade-in {
          opacity: 0;
          animation: fadeIn 1s ease-out forwards;
        }
        .animate-slide-up {
          opacity: 0;
          transform: translateY(30px);
          animation: slideUp 0.8s ease-out forwards;
        }
        @keyframes fadeIn {
          to { opacity: 1; }
        }
        @keyframes slideUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Scroll reveal animations */
        .scroll-reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .visible .scroll-reveal {
          opacity: 1;
          transform: translateY(0);
        }

        /* Gradient animated text */
        .gradient-text {
          background: linear-gradient(90deg, 
            var(--color-primary) 0%, 
            var(--color-secondary) 50%, 
            var(--color-primary) 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 8s ease-in-out infinite;
        }
        @keyframes gradientShift {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 100% center; }
        }

        /* Magnetic Button Effect */
        .magnetic-btn {
          position: relative;
          overflow: hidden;
          transform-style: preserve-3d;
          transition: transform 0.3s ease;
        }
        .magnetic-btn .btn-text {
          position: relative;
          z-index: 2;
        }
        .magnetic-btn .btn-bg {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at center, rgba(255,255,255,0.2), transparent);
          transform: scale(0);
          transition: transform 0.5s ease;
          z-index: 1;
        }
        .magnetic-btn:hover .btn-bg {
          transform: scale(2.5);
        }
        .magnetic-btn::after {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(45deg, 
            transparent, 
            rgba(255,255,255,0.1), 
            transparent);
          transform: translateX(-100%);
          transition: transform 0.6s ease;
        }
        .magnetic-btn:hover::after {
          transform: translateX(100%);
        }
        .magnetic-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        }

        .page-hero {
          position: relative;
          padding: 88px 20px 64px;
          overflow: hidden;
          color: #fff;
        }
        .hero-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(160deg, var(--color-bg-dark) 0%, #1a252f 100%);
          z-index: 0;
        }
        .page-hero > .container {
          position: relative;
          z-index: 1;
        }
        .page-hero-label {
          display: block;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--color-primary);
          margin-bottom: 8px;
        }
        .page-title { margin: 0 0 12px; font-size: 2rem; font-weight: 700; letter-spacing: -0.02em; }
        .page-intro { margin: 0; opacity: 0.92; font-size: 1.05rem; }
        .who-section { 
          background: #f5f6f8;
          position: relative;
          overflow: hidden;
        }
        .who-section::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(var(--color-primary-rgb), 0.03) 0%, transparent 70%);
          animation: rotateGradient 20s linear infinite;
        }
        @keyframes rotateGradient {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin: 36px auto 0;
          max-width: 920px;
        }
        .benefit-item {
          padding: 28px 20px;
          background: #fff;
          border-radius: var(--radius);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--color-border);
          position: relative;
          overflow: hidden;
          transform-style: preserve-3d;
        }
        .benefit-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(var(--color-primary-rgb), 0.1), transparent);
          transition: left 0.5s ease;
          z-index: 1;
        }
        .benefit-item:hover::before {
          left: 100%;
        }
        
        /* Benefit card glow effect */
        .benefit-glow {
          position: absolute;
          inset: -2px;
          background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
          border-radius: var(--radius);
          opacity: 0;
          z-index: -1;
          filter: blur(10px);
          transition: opacity 0.3s ease;
        }
        .benefit-item:hover .benefit-glow {
          opacity: 0.4;
        }

        .benefit-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          margin: 0 auto 12px;
          color: var(--color-secondary);
          transition: transform 0.3s ease;
          position: relative;
          z-index: 2;
        }
        .benefit-item:hover .benefit-icon {
          transform: scale(1.1) rotate(5deg);
          animation: iconPulse 0.6s ease-in-out;
        }
        @keyframes iconPulse {
          0%, 100% { transform: scale(1.1) rotate(5deg); }
          50% { transform: scale(1.2) rotate(-5deg); }
        }
        .benefit-icon .benefit-icon-svg { width: 28px; height: 28px; display: block; }
        .benefit-icon .benefit-icon-svg svg { width: 100%; height: 100%; }
        .benefit-title { 
          margin: 0; 
          font-size: 1.05rem; 
          font-weight: 700; 
          color: #1a1a1a;
          position: relative;
          z-index: 2;
        }
        .team-section {
          position: relative;
          background: #fff;
          overflow: hidden;
        }
        .team-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300a0e3' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.3;
          z-index: 0;
        }
        .team-section > .container {
          position: relative;
          z-index: 1;
        }
        .team-card {
          max-width: 800px;
          margin: 32px auto 0;
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 24px;
          align-items: start;
          background: #fff;
          padding: 32px;
          border-radius: var(--radius);
          box-shadow: var(--shadow-md);
          border: 1px solid var(--color-border);
          transition: box-shadow 0.3s ease, transform 0.3s ease;
        }
        .team-card:hover {
          box-shadow: var(--shadow-lg);
          transform: translateY(-4px);
        }
        .team-photos {
          position: relative;
          overflow: hidden;
          border-radius: var(--radius-sm);
          width: 80%;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transition: box-shadow 0.3s ease, transform 0.3s ease;
        }
        .team-photos:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          transform: scale(1.02);
        }
        .team-photos .team-img {
          width: 100%;
          border-radius: var(--radius-sm);
          display: block;
          transition: opacity 0.35s ease, filter 0.35s ease, transform 0.5s ease;
        }
        .team-photos .team-img-default {
          filter: grayscale(100%);
        }
        .team-photos .team-img-hover {
          position: absolute;
          top: 0;
          left: 0;
          opacity: 0;
          filter: grayscale(0);
        }
        .team-photos:hover .team-img-default { 
          opacity: 0;
          transform: scale(1.05);
        }
        .team-photos:hover .team-img-hover { 
          opacity: 1;
          transform: scale(1.05);
        }
        .team-role { 
          margin: 0 0 16px; 
          color: var(--color-secondary); 
          font-size: 1rem; 
          font-weight: 600;
          position: relative;
          display: inline-block;
        }
        .team-role::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 0;
          width: 0;
          height: 2px;
          background: var(--color-primary);
          transition: width 0.3s ease;
        }
        .team-card:hover .team-role::after {
          width: 100%;
        }
        .team-info p { 
          color: var(--color-text-muted); 
          font-size: 0.98rem;
          line-height: 1.7;
        }
        .team-social { 
          display: flex; 
          gap: 14px; 
          margin-top: 20px;
        }
        .team-social a { 
          color: var(--color-link); 
          transition: color 0.2s ease, transform 0.3s ease;
          display: inline-block;
        }
        .team-social a:hover { 
          color: var(--color-primary);
          transform: translateY(-3px) scale(1.1);
        }
        .join-section { 
          background: linear-gradient(135deg, #f5f6f8 0%, #e8eaed 100%);
          position: relative;
          overflow: hidden;
        }
        .join-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(var(--color-secondary-rgb), 0.05) 0%, transparent 70%);
          animation: rotateGradient 25s linear infinite reverse;
        }

        /* Reduced motion preferences */
        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in,
          .animate-slide-up,
          .scroll-reveal {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
          .gradient-text,
          .who-section::before,
          .join-section::before {
            animation: none !important;
          }
          .custom-cursor,
          .custom-cursor-dot {
            display: none;
          }
          .scroll-progress-bar {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .custom-cursor,
          .custom-cursor-dot {
            display: none;
          }
          .scroll-progress-bar {
            height: 2px;
          }
          .benefits-grid { grid-template-columns: repeat(2, 1fr); }
          .team-card { 
            grid-template-columns: 1fr;
            padding: 24px;
          }
          .team-photos {
            width: 100%;
            max-width: 300px;
            margin: 0 auto;
          }
        }
      `}</style>
    </>
  );
}
